/**
 * Runtime Configuration Context
 * Fetches config.json at startup and provides it to the app
 */
import { initializeAuthAdapter } from '@mdvip/shared/auth/createAuthAdapter';
import { initializeGraphQLClient } from '@mdvip/shared/lib/graphql-client';
import { initializeTelemetry } from '@mdvip/shared/lib/telemetry';
import type { AppConfig } from '@mdvip/shared/types/config';
import { getEnvironment } from '@mdvip/shared/types/config';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfigContextType {
  config: AppConfig;
  isLoading: boolean;
  error: Error | null;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const { t } = useTranslation('members');
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    async function loadConfig() {
      try {
        // Fetch config.json with cache-busting to ensure fresh config
        const response = await fetch('/config.json', {
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
        }

        const configData: AppConfig = await response.json();
        setConfig(configData);

        // Initialize GraphQL client with config
        initializeGraphQLClient(configData);
        // Initialize auth adapter — must be awaited so getAuthAdapter() is ready
        // before ConfigContext renders its children (AuthProvider, ProtectedRoute, etc.)
        await initializeAuthAdapter(configData);

        // Initialize telemetry with derived environment
        const environment = getEnvironment(configData);
        initializeTelemetry(
          configData.APPLICATIONINSIGHTS_CONNECTION_STRING,
          environment,
          configData.MDVIP_API_BASE_URL,
        );

        setIsInitialized(true);
      } catch (err) {
        console.error('Failed to load application configuration:', err);
        setError(err instanceof Error ? err : new Error('Unknown error loading config'));
      } finally {
        setIsLoading(false);
      }
    }

    loadConfig();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t('config.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !config || !isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-6">
          <h1 className="text-xl font-semibold text-destructive-foreground mb-2">{t('config.errorTitle')}</h1>
          <p className="text-muted-foreground">{error?.message || t('config.errorFallback')}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            {t('config.retry')}
          </button>
        </div>
      </div>
    );
  }

  return <ConfigContext value={{ config, isLoading, error }}>{children}</ConfigContext>;
}

export function useConfig(): AppConfig {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context.config;
}

export { ConfigContext };
